import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Calendar, ArrowRight, Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { articulosBlog as mockArticulos } from '../data/mock';

const BlogSection = () => {
  const navigate = useNavigate();
  const [articulos, setArticulos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    cargarArticulos();
  }, []);

  const cargarArticulos = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/blog?publicado=true&limit=3`);
      const data = await response.json();

      if (data.success && data.articulos.length > 0) {
        setArticulos(data.articulos);
      } else {
        // Fallback a datos mock si no hay artículos en la BD
        setArticulos(mockArticulos.slice(0, 3));
      }
    } catch (err) {
      console.error('Error cargando artículos:', err);
      // Fallback a datos mock
      setArticulos(mockArticulos.slice(0, 3));
    } finally {
      setLoading(false);
    }
  };

  const irAlArticulo = (slug) => {
    navigate(`/blog/${slug}`);
  };

  const irAlBlog = () => {
    navigate('/blog');
  };

  const formatearFecha = (fecha) => {
    const date = new Date(fecha);
    return date.toLocaleDateString('es-ES', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <section id="blog" className="py-16 md:py-24">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-2 mb-4">
            <BookOpen size={40} className="text-[var(--brand-dark)]" />
            <h2 className="heading-1">Blog de osteopatía</h2>
          </div>
          <p className="body-large max-w-2xl mx-auto">
            Artículos sobre tratamientos osteopáticos para diferentes lesiones y dolencias
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="animate-spin text-[var(--brand-dark)]" size={48} />
            <span className="ml-4 body-large">Cargando artículos...</span>
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {articulos.map((articulo) => (
              <Card 
                key={articulo.id || articulo.slug} 
                className="network-card cursor-pointer"
                onClick={() => irAlArticulo(articulo.slug)}
                data-testid={`blog-card-${articulo.slug}`}
              >
                <CardHeader className="p-0">
                  <div className="relative aspect-video rounded-t-[32px] overflow-hidden bg-[var(--bg-section)]">
                    <img 
                      src={articulo.imagen} 
                      alt={articulo.titulo}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-3 left-3 bg-[var(--brand-primary)] text-[var(--brand-dark)] px-3 py-1 rounded-full text-sm font-semibold">
                      {articulo.categoria}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2 text-[var(--text-light)] mb-3">
                    <Calendar size={16} />
                    <span className="body-small">{formatearFecha(articulo.fecha || articulo.created_at)}</span>
                  </div>
                  <CardTitle className="heading-3 mb-3 line-clamp-2">{articulo.titulo}</CardTitle>
                  <CardDescription className="body-medium line-clamp-3">
                    {articulo.extracto}
                  </CardDescription>
                  <div className="mt-4">
                    <Button variant="link" className="p-0 h-auto text-[var(--brand-dark)] font-semibold">
                      Leer más <ArrowRight className="ml-1 h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <div className="text-center">
          <Button className="btn-secondary" onClick={irAlBlog} data-testid="ver-todos-articulos">
            Ver todos los artículos
          </Button>
        </div>
      </div>
    </section>
  );
};

export default BlogSection;
